import { useState, useMemo } from "react";
import { Flame, Table2 } from "lucide-react";
import { ChartCard, SortTh, tableClass } from "./ui";
import { cn } from "@/lib/utils";

const UsageTable = ({ daily }: any) => {
  const [sortKey, setSortKey] = useState("date");
  const [sortDir, setSortDir] = useState("desc");

  const busiestDate = useMemo(() => {
    if (!daily || daily.length === 0) return null;
    let max = 0;
    let maxDate = null;
    for (const d of daily) {
      const calls = d.total_calls || 0;
      if (calls > max) {
        max = calls;
        maxDate = d.date;
      }
    }
    return maxDate;
  }, [daily]);

  const sorted = useMemo(() => {
    if (!daily || daily.length === 0) return [];
    const copy = [...daily];
    copy.sort((a, b) => {
      let aVal = a[sortKey];
      let bVal = b[sortKey];
      if (typeof aVal === "string") {
        return sortDir === "asc" ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      aVal = aVal || 0;
      bVal = bVal || 0;
      return sortDir === "asc" ? aVal - bVal : bVal - aVal;
    });
    return copy;
  }, [daily, sortKey, sortDir]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  const columns = [
    { key: "date", label: "Date" },
    { key: "total_calls", label: "Questions Asked" },
    { key: "total_tokens", label: "AI Usage" },
  ];

  if (!daily || daily.length === 0) {
    return null;
  }

  return (
    <ChartCard icon={<Table2 />} title="Daily Breakdown" description="Day-by-day numbers. Click a column to sort.">
      <div className="usage-table-container overflow-x-auto rounded-lg border border-border">
        <table className={tableClass}>
          <thead className="bg-muted/50">
            <tr>
              {columns.map((col) => (
                <SortTh key={col.key} label={col.label} active={sortKey === col.key} dir={sortDir} onClick={() => handleSort(col.key)} />
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map((row) => (
              <tr key={row.date} className={cn(row.date === busiestDate && "busiest-day-row bg-primary/5")}>
                <td>
                  {row.date
                    ? new Date(row.date + "T00:00:00").toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })
                    : ""}
                </td>
                <td className="num-formatted tabular-nums">
                  {(row.total_calls || 0).toLocaleString()}
                  {row.date === busiestDate && (
                    <span className="busiest-badge ms-2 inline-flex items-center gap-1 rounded-full bg-warning-500/15 px-2 py-0.5 text-xs font-medium text-warning-700" title="Busiest day in this period">
                      <Flame className="size-3" aria-hidden="true" /> Busiest
                    </span>
                  )}
                </td>
                <td className="num-formatted tabular-nums">{(row.total_tokens || 0).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </ChartCard>
  );
};

export default UsageTable;
